import { useMutation, useQueryClient } from '@tanstack/react-query';
import Toast from '../components/Toast';
import { jsonDeleteResponse, jsonPost, jsonPut } from '../api/jsonRequest';
import { queryKeys } from '../query/queryKeys';
import { buildPollUrl } from '../query/queryParams';
import { usePollQuery } from '../query/usePollQuery';

export function useCompanyPrices({
    companyId,
    enabled = true,
    pauseRefetch = false,
}) {
    const url = buildPollUrl('/api/Company/GetAllPricesForCompany', companyId);

    return usePollQuery({
        queryKey: queryKeys.prices.company(companyId),
        url,
        enabled: Boolean(companyId) && enabled,
        pauseRefetch,
    });
}

export function useBotmarkPrices({ enabled = true, pauseRefetch = false } = {}) {
    return usePollQuery({
        queryKey: queryKeys.prices.botmark(),
        url: '/api/Company/GetBotmarkPrice',
        enabled,
        pauseRefetch,
    });
}

export function useCreatePrice() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload) => {
            const res = await jsonPost('/api/Company/CreatePrice', payload);
            if (res.status !== 200) {
                throw new Error('Creation failed');
            }
            return res.data;
        },
        onSuccess: () => {
            Toast.fire({
                title: 'Price Updated',
                icon: 'success',
            });
            void queryClient.invalidateQueries({ queryKey: queryKeys.prices.all() });
        },
        onError: () => {
            Toast.fire({
                title: 'Creation failed',
                icon: 'error',
            });
        },
    });
}

export function useUpdatePrice() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload) => {
            const res = await jsonPut('/api/Company/UpdatePrice', payload);
            if (res.status !== 200) {
                throw new Error('Creation failed');
            }
            return res.data;
        },
        onSuccess: () => {
            Toast.fire({
                title: 'Price Updated',
                icon: 'success',
            });
            void queryClient.invalidateQueries({ queryKey: queryKeys.prices.all() });
        },
        onError: () => {
            Toast.fire({
                title: 'Creation failed',
                icon: 'error',
            });
        },
    });
}

export function useDeletePrice() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id }) => {
            const res = await jsonDeleteResponse(`/api/Company/DeletePrice/${id}`);
            if (res.status !== 200) {
                throw new Error('Delete failed');
            }
            return res.data;
        },
        onSuccess: () => {
            Toast.fire({
                title: 'Deleted',
                icon: 'success',
            });
            void queryClient.invalidateQueries({ queryKey: queryKeys.prices.all() });
        },
        onError: () => {
            Toast.fire({
                title: 'Delete failed',
                icon: 'error',
            });
        },
    });
}
